"use client";

import { FC, useCallback, useEffect, useState } from "react";

interface ServiceWorkerProps {
  swPath?: string;
  onRegistered?: (registration: ServiceWorkerRegistration) => void;
  onError?: (error: Error) => void;
  onUpdated?: (registration: ServiceWorkerRegistration) => void;
}

declare global {
  interface Window {
    __SW_REGISTRATION__?: ServiceWorkerRegistration;
    __SW_UPDATE__?: () => Promise<void>;
    __SW_SKIP_WAITING__?: () => void;
  }
}

export const ServiceWorker: FC<ServiceWorkerProps> = ({
  swPath = "/sw-eva.js",
  onRegistered,
  onError,
  onUpdated,
}) => {
  const [registration, setRegistration] =
    useState<ServiceWorkerRegistration | null>(null);
  const [_isLoading, setIsLoading] = useState(false);
  const [_error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (typeof window === "undefined" || !("serviceWorker" in navigator)) {
      return;
    }

    const registerServiceWorker = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const registration = await navigator.serviceWorker.register(swPath, {
          scope: "/",
        });

        setRegistration(registration);
        onRegistered?.(registration);

        // Handle service worker updates
        registration.addEventListener("updatefound", () => {
          const newWorker = registration.installing;

          if (newWorker) {
            newWorker.addEventListener("statechange", () => {
              if (newWorker.state === "installed") {
                if (navigator.serviceWorker.controller) {
                  // New update available
                  onUpdated?.(registration);
                } else {
                  // First install
                }
              }
            });
          }
        });

        // Handle controller changes
        navigator.serviceWorker.addEventListener("controllerchange", () => {
          window.location.reload();
        });
      } catch (err) {
        const error =
          err instanceof Error
            ? err
            : new Error("Service worker registration failed");
        setError(error);
        onError?.(error);
      } finally {
        setIsLoading(false);
      }
    };

    registerServiceWorker();
  }, [swPath, onRegistered, onError, onUpdated]);

  // Update service worker when new version is available
  const updateServiceWorker = useCallback(async () => {
    if (!registration) return;

    try {
      await registration.update();
    } catch (err) {
      // Silent fail for update errors
    }
  }, [registration]);

  // Skip waiting and activate new service worker immediately
  const skipWaiting = useCallback(() => {
    if (!registration?.waiting) return;

    registration.waiting.postMessage({ type: "SKIP_WAITING" });
  }, [registration]);

  // Development helpers
  useEffect(() => {
    if (process.env.NODE_ENV === "development") {
      // Auto-update service worker in development
      const interval = setInterval(() => {
        updateServiceWorker();
      }, 60000); // Check every minute

      return () => clearInterval(interval);
    }
  }, [updateServiceWorker]);

  // Expose registration and methods for external use
  useEffect(() => {
    if (registration) {
      window.__SW_REGISTRATION__ = registration;
      window.__SW_UPDATE__ = updateServiceWorker;
      window.__SW_SKIP_WAITING__ = skipWaiting;
    }
  }, [registration, updateServiceWorker, skipWaiting]);

  // Don't render anything, this is a service component
  return null;
};
